/**
 * 69.5, the HQ's root mast (lane E; decision 165: a strike squad demolishes the enemy HQ, and when it falls its side's
 * arrivals stop and its web loses its root): a lattice mast beside every builder's HQ, standing over its roof, with the
 * web's lamp and the team's pennant on top — what says "the root of the web" from the diorama and the overview.
 *
 * - **The web** (69.2): `setHqRootLink(m, link, t)` — the relay's rule (relayModel paintLinkLight): linked, steady in the
 *   team's colour (the root is in its own web while it stands); off, dark. The renderer's `lightWeb` drives it with the
 *   relays' lights, from the same state.
 * - **Forms** (63.4's pattern): `whole`; `damaged` — the mast leaning off true, the upper braces on one face shot away,
 *   the lamp flickering; `destroyed` — the mast down along the ground beside the ruin (`fallen:mast`), the lamp dark.
 *
 * Built in person units (a person is 1.8), beside a building measured in its own frame (`withHqRoot`). Presentation only.
 */
import * as THREE from 'three';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import { paintLinkLight, type RelayForm, type RelayLink } from './relayModel';

export type HqRootForm = RelayForm;
export const HQ_ROOT = {
  /** the mast's top over the building's (person units), and its least height */
  over: 3.6, least: 9,
  /** the legs' spread at the foot and the top, their thickness; a brace every `brace` up */
  foot: 0.95, top: 0.32, leg: 0.1, brace: 1.5,
  /** the lamp's radius (read from the diorama), the pennant's size */
  lamp: 0.42, pennant: [1.4, 0.7] as const,
  /** standing off the building's front-left corner (the windsock has the front-right) */
  off: 0.7,
  /** damaged: the lean (radians); destroyed: the mast's fall */
  lean: 0.16, fall: 1.52,
};

/** The root mast. Once baked (`withHqRoot`), `mast` is the lattice as built (its meshes merged); `lamp` stays its own mesh, drawn with `lightMat`. */
export interface HqRootModel {
  root: THREE.Group;
  mast: THREE.Group;
  lamp: THREE.Mesh;
  lightMat: THREE.MeshLambertMaterial;
  form: HqRootForm;
  palette: UnitPalette;
  height: number;
  /** what the lamp last showed (paintLinkLight) */
  shown?: string;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
/** A box `t` thick from a to b. */
function strut(parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, t: number, color: string): THREE.Mesh {
  const d = b.clone().sub(a), len = d.length();
  const m = new THREE.Mesh(new THREE.BoxGeometry(t, len, t), mat(color));
  m.position.copy(a).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  parent.add(m);
  return m;
}

/** The root mast, `height` tall (person units), in its form. */
export function buildHqRoot(p: UnitPalette, height: number, opts: { form?: HqRootForm } = {}): HqRootModel {
  const form = opts.form ?? 'whole', R = HQ_ROOT;
  const root = new THREE.Group(); root.name = 'hqRoot';
  const mast = new THREE.Group(); mast.name = form === 'destroyed' ? 'fallen:mast' : 'mast'; root.add(mast);
  const legAt = (k: number, y: number) => { const r = R.foot + (R.top - R.foot) * (y / height), a = (k / 3) * Math.PI * 2 + Math.PI / 6; return new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r); };
  for (let k = 0; k < 3; k++) strut(mast, legAt(k, 0), legAt(k, height), R.leg, p.metal);
  // the braces: a zigzag up each face (the damaged form's upper third on one face shot away)
  const steps = Math.max(2, Math.round(height / R.brace));
  for (let k = 0; k < 3; k++) for (let i = 0; i < steps; i++) {
    if (form === 'damaged' && k === 0 && i >= steps * 0.6) continue;
    const y0 = (i / steps) * height, y1 = ((i + 1) / steps) * height;
    strut(mast, legAt(k, y0), legAt((k + 1) % 3, y1), R.leg * 0.55, p.metal);
    strut(mast, legAt(k, y1), legAt((k + 1) % 3, y1), R.leg * 0.55, p.metal);
  }
  // the head: a platform, the lamp in its cage, the pennant on a short pole
  const head = new THREE.Group(); head.position.y = height; mast.add(head);
  const plat = new THREE.Mesh(new THREE.CylinderGeometry(R.top * 1.9, R.top * 1.9, 0.12, 6), mat(p.dark)); head.add(plat);
  const lightMat = new THREE.MeshLambertMaterial({ color: p.flash, emissive: new THREE.Color(p.flash), emissiveIntensity: 1 });
  const lamp = new THREE.Mesh(new THREE.SphereGeometry(R.lamp, 12, 8), lightMat); lamp.position.y = 0.06 + R.lamp; head.add(lamp);
  lamp.userData.noBake = true;   // (69.5e: every bake passes it over — the building's own, the renderer's — or the web could not set it)
  for (let k = 0; k < 4; k++) { const a = (k / 4) * Math.PI * 2; strut(head, new THREE.Vector3(Math.cos(a) * R.lamp * 1.1, 0.06, Math.sin(a) * R.lamp * 1.1), new THREE.Vector3(Math.cos(a) * R.lamp * 0.4, 0.1 + R.lamp * 2.15, Math.sin(a) * R.lamp * 0.4), 0.04, p.dark); }
  strut(head, new THREE.Vector3(-R.top * 1.6, 0, 0), new THREE.Vector3(-R.top * 1.6, 2.1, 0), 0.06, p.metal);
  const flag = new THREE.Mesh(new THREE.BoxGeometry(R.pennant[0], R.pennant[1], 0.03), mat(p.flash));
  flag.position.set(-R.top * 1.6 - R.pennant[0] / 2, 2.1 - R.pennant[1] / 2, 0); head.add(flag);
  if (form === 'damaged') { mast.rotation.z = R.lean; flag.rotation.z = -0.4; }
  if (form === 'destroyed') { mast.rotation.z = R.fall; mast.position.y = R.leg; flag.visible = false; }
  const m: HqRootModel = { root, mast, lamp, lightMat, form, palette: p, height };
  setHqRootLink(m, form === 'destroyed' ? 'off' : 'linked', 0);
  return m;
}

/** The web on the lamp (the relay's rule): linked steady, unlinked the amber blink, off dark; damaged, it flickers. */
export function setHqRootLink(m: HqRootModel, link: RelayLink, t: number): void {
  paintLinkLight(m, m.form === 'damaged', m.form !== 'destroyed' && link !== 'off', link, t);
}

/**
 * The root mast stood off `building`'s front-left corner (its box in its own frame; the windsock has the other), over its
 * roof by HQ_ROOT.over. Baked; returns it.
 */
export function withHqRoot(building: THREE.Group, p: UnitPalette, form: HqRootForm): HqRootModel {
  building.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(building.matrixWorld).invert();
  const b = new THREE.Box3();
  building.traverse(o => { if ((o as THREE.Mesh).isMesh) b.union(new THREE.Box3().setFromObject(o, true).applyMatrix4(inv)); });
  const m = buildHqRoot(p, Math.max(HQ_ROOT.least, b.max.y + HQ_ROOT.over), { form });
  m.root.position.set(b.min.x - HQ_ROOT.off, 0, b.max.z + HQ_ROOT.off);
  if (form === 'destroyed') m.root.rotation.y = -0.6;   // (fallen away from the ruin's front)
  bakeStaticParts(m.root, { exclude: [m.lamp] });   // (the lamp stays its own: the web sets its material — any later bake of the building must exclude it too)
  building.add(m.root);
  return m;
}
